// bookingHistory.js
// -----------------------------------------------------------------------------
// One bookingSlot's story, oldest first: who booked it, every move, cancel,
// correction and SO attach in its `history`, and every propose-and-confirm
// request in `proposalHistory` plus the one still open -- filed by whom,
// approved, rejected or withdrawn by whom. Both จองวันส่งสินค้า (the expanded
// row) and ค้นหาการจอง (the order card) print these lines, so the two pages
// cannot tell the same booking's story differently.
//
// Pure -- no Firestore, no Vue -- asserted by `npm run test:search`. Names are
// resolved by the caller's `nameOf` (the `$personName` directory), so an
// account that moved to a user ID still reads as one person.
// -----------------------------------------------------------------------------
const moment = require('moment-timezone')
const R = require('./bookingRules')
const U = require('./upcountry')

// Buddhist-era, as every other date a shop reads on this app.
function thaiDate (ymd) {
  const m = moment.tz(ymd, 'YYYY-MM-DD', R.TZ)
  return m.isValid() ? m.format('DD/MM/') + (m.year() + 543) : String(ymd || '-')
}

// Stamps are written with toISOString (UTC); read them in Bangkok time.
function thaiStamp (iso) {
  const m = moment.tz(iso, R.TZ)
  return m.isValid() ? `${m.format('DD/MM/')}${m.year() + 543} ${m.format('HH:mm')}` : ''
}

function slotText (date, slot) {
  const label = R.SLOT_LABEL_TH[slot]
  return label ? `${thaiDate(date)} ${label}` : thaiDate(date)
}

function reasonText (v) {
  return (R.MOVE_REASONS.find(r => r.value === v) || {}).text || v || '-'
}

// '(ว่าง)' rather than an empty gap: an arrow with nothing before it reads as a
// rendering fault, where '(ว่าง)' reads as the fact it is.
function changeValue (v) {
  const t = String(v == null ? '' : v)
  if (!t) return '(ว่าง)'
  return t.length > 60 ? t.slice(0, 60) + '…' : t
}

function changesText (changes) {
  return (changes || []).map(c =>
    `${R.EDIT_FIELD_LABEL_TH[c.field] || c.field}: ${changeValue(c.from)} → ${changeValue(c.to)}`).join(', ')
}

function noteText (note) {
  return note ? ` (${note})` : ''
}

const INSTALL_MODE_TH = { install: 'ติดตั้ง', drop: 'ส่งอย่างเดียว', unknown: 'ยังไม่ทราบ' }

function weekText (monday) {
  return monday ? U.weekLabel(monday) : '-'
}

// One `history` entry -> the line's text, without its time.
function historyText (h, nameOf) {
  const by = nameOf(h.by) || '-'
  if (h.action === 'edit' || h.action === 'pin') {
    // 'pin' is the same correction made from carBooking's slot dialog, after
    // the booking was planned -- see bookingService.setPin.
    const where = h.action === 'pin' ? ' (จากคิวรถเล็ก)' : ''
    return `แก้ไข${where} ${changesText(h.changes)} · โดย ${by}`
  }
  if (h.action === 'attachSo') {
    const parts = changesText(h.changes)
    // Only an attach can move a booking across the service-area line after it
    // was made, which moves it on or off the day's car capacity.
    const area = h.areaMovedTo === 'out'
      ? ' · เปลี่ยนเป็นนอกพื้นที่จัดส่ง'
      : (h.areaMovedTo === 'in' ? ' · เปลี่ยนเป็นในพื้นที่จัดส่ง' : '')
    return `ใส่เลขที่ SO ${h.soNo || '-'}${parts ? ` · ${parts}` : ''}${area} · โดย ${by}`
  }
  if (h.action === 'attachRt') {
    return `เพิ่มรับคืน RT ${h.rtNo || '-'} · โดย ${by}`
  }
  // One destination, several bookings (visitRules). `with` is the other SO.
  if (h.action === 'linkVisit' || h.action === 'joinVisit') {
    return `รวมเป็นจุดส่งเดียวกับ ${h.with ? 'SO ' + h.with : 'นัดหมาย'} · โดย ${by}`
  }
  if (h.action === 'unlinkVisit') {
    return `แยกออกจากจุดส่งเดียวกัน · โดย ${by}`
  }
  if (h.action === 'attachTrip') {
    return `จัดเข้าเส้นทางนอกพื้นที่ · โดย ${by}`
  }
  if (h.action === 'cancel') {
    return `ยกเลิก — ${reasonText(h.reason)}${noteText(h.note)} · โดย ${by}`
  }
  if (h.action === 'move' && h.from && h.to) {
    // A move confirmed from a request names both people: who asked for it and
    // who approved it. A direct move has only the one.
    const who = h.proposedBy
      ? `ขอโดย ${nameOf(h.proposedBy) || '-'} · อนุมัติโดย ${by}`
      : `โดย ${by}`
    return `ย้าย ${slotText(h.from.date, h.from.slot)} → ${slotText(h.to.date, h.to.slot)} — ` +
      `${reasonText(h.reason)}${noteText(h.note)}${h.leftVisit ? ' · แยกออกจากจุดส่งเดียวกัน' : ''} · ${who}`
  }
  // Up-country (large truck) orders: a week, a trip and a plate, never a slot.
  if (h.action === 'upBook') return `จองส่งรถใหญ่ต่างจังหวัด ตั้งแต่สัปดาห์ ${weekText(h.week)} · โดย ${by}`
  if (h.action === 'upWeek') return `เปลี่ยนสัปดาห์ ${weekText(h.from)} → ${weekText(h.to)} · โดย ${by}`
  if (h.action === 'upFix') return `จัดเข้ารอบรถใหญ่ ${thaiDate(h.date)} รถ ${h.regNum || '-'} · โดย ${by}`
  if (h.action === 'upPostpone') return `เลื่อนไปรอบถัดไป (สัปดาห์ ${weekText(h.week)}) · โดย ${by}`
  if (h.action === 'upTripCancelled') return `รอบรถใหญ่ถูกยกเลิก กลับไปรอจัดรอบ · โดย ${by}`
  if (h.action === 'upRemove') return `นำออกจากรอบรถใหญ่ ${thaiDate(h.from)} กลับไปรอจัดรอบ · โดย ${by}`
  if (h.action === 'upPlanClear') return `ยกเลิกแผนเส้นทาง: วันส่ง ${thaiDate(h.from)} → ${thaiDate(h.to)} (วันออกรถ) · โดย ${by}`
  if (h.action === 'upPlan') return `วางแผนเส้นทาง: วันส่ง ${thaiDate(h.from)} → ${thaiDate(h.to)} · โดย ${by}`
  if (h.action === 'upRefresh') {
    // Only what actually changed, in the units the page shows.
    const f = h.from || {}
    const t = h.to || {}
    const parts = []
    if (Number(f.totalVolume) !== Number(t.totalVolume)) parts.push(`ปริมาตร ${f.totalVolume} → ${t.totalVolume} CBM`)
    if (Number(f.itemCount) !== Number(t.itemCount)) parts.push(`จำนวน ${f.itemCount} → ${t.itemCount} ชิ้น`)
    if (Number(f.orderValue) !== Number(t.orderValue)) parts.push(`มูลค่า ฿${f.orderValue} → ฿${t.orderValue}`)
    return `อัปเดตข้อมูลจาก SAP${parts.length ? ': ' + parts.join(', ') : ''} · โดย ${by}`
  }
  if (h.action === 'upInstallMode') {
    return `การติดตั้ง: ${INSTALL_MODE_TH[h.to] || INSTALL_MODE_TH.unknown} · โดย ${by}`
  }
  return `${h.action || 'เปลี่ยนแปลง'} · โดย ${by}`
}

// A request as it was filed.
function filedText (p, nameOf) {
  const what = p.kind === 'cancel' ? 'ขอยกเลิก' : `ขอเลื่อนไป ${slotText(p.date, p.slot)}`
  return `${what} — ${reasonText(p.reason)}${noteText(p.note)} · ขอโดย ${nameOf(p.by) || '-'}`
}

// The events one request contributes: filed, then how it ended. A confirmed
// request ends in the booking's own `history` (the move or cancel names the
// proposer and approver), so it adds no second line here.
function proposalEvents (p, nameOf, open, today) {
  const out = [{ at: p.at, text: filedText(p, nameOf) }]
  if (p.status === 'pending') {
    const expired = p.kind === 'move' && p.date < today
    out[0].text += expired ? ' · หมดอายุ (ยังไม่ได้ปิดคำขอ)' : ' · รอยืนยัน'
    return out
  }
  const by = nameOf(p.decidedBy) || '-'
  if (p.status === 'rejected') {
    let told = ''
    if (p.customerTold) told = ` · แจ้งลูกค้าแล้ว${p.toldBy ? ` (โดย ${nameOf(p.toldBy) || '-'})` : ''}`
    else if (open) told = ' · ยังไม่ได้แจ้งลูกค้า'
    out.push({ at: p.decidedAt, text: `ปฏิเสธคำขอ — ${p.rejectReason || '-'} · โดย ${by}${told}` })
  } else if (p.status === 'withdrawn') {
    out.push({ at: p.decidedAt, text: `ถอนคำขอ · โดย ${by}` })
  }
  return out
}

// The whole story, oldest first: [{ at, when, text }]. `booking` is a
// bookingSlot document (or the fields of one); nothing on it is required.
function timeline (booking, opts) {
  const b = booking || {}
  const o = opts || {}
  const nameOf = o.nameOf || (x => x || '')
  const today = o.today || R.todayStr()
  const events = []

  if (b.createdAt) {
    // Where it was first booked: the first move's origin, or where it still is.
    if (b.fleet === 'large') {
      // Up-country: promised a week, not a date (b.date is the trip's, once fixed).
      const up = (b.history || []).find(h => h.action === 'upBook')
      const week = (up && up.week) || b.earliestWeek
      events.push({ at: b.createdAt, text: `จองส่งรถใหญ่ต่างจังหวัด ตั้งแต่สัปดาห์ ${weekText(week)} · โดย ${nameOf(b.user) || '-'}` })
    } else {
      const first = (b.history || []).find(h => h.action === 'move' && h.from)
      const at = first ? first.from : { date: b.date, slot: b.slot }
      events.push({ at: b.createdAt, text: `จองวันส่ง ${slotText(at.date, at.slot)} · โดย ${nameOf(b.user) || '-'}` })
    }
  }
  (b.history || []).forEach(h => events.push({ at: h.at, text: historyText(h, nameOf) }))
  ;(b.proposalHistory || []).forEach(p => proposalEvents(p, nameOf, false, today).forEach(e => events.push(e)))
  if (b.proposal && (b.proposalStatus === 'pending' || b.proposalStatus === 'rejectedUntold')) {
    proposalEvents(b.proposal, nameOf, true, today).forEach(e => events.push(e))
  }

  // Stable by time: two entries with one stamp keep the order written.
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, c) => String(a.e.at || '').localeCompare(String(c.e.at || '')) || a.i - c.i)
    .map(({ e }) => ({ at: e.at || '', when: thaiStamp(e.at), text: e.text }))
}

// "text · when" -- the one-line form both pages print.
function lineOf (entry) {
  return entry.when ? `${entry.text} · ${entry.when}` : entry.text
}

module.exports = { historyText, timeline, lineOf, thaiDate, thaiStamp }
